import React from 'react';
import {
  Box, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Tooltip, IconButton
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { F } from '../../../utils/qualFmt';
import { computeWinScore, computeGapScore } from '../../../utils/scoring';

/** Flatten segmentation_data into a readable single line (handles primitives, arrays, nested) */
function formatSegmentation(seg) {
  if (!seg || typeof seg !== 'object') return '—';
  const entries = Object.entries(seg)
    .map(([k, v]) => {
      if (v == null) return null;
      if (Array.isArray(v)) {
        const list = v.map(x => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ');
        return `${k}: ${list}`;
      }
      if (typeof v === 'object') {
        const inner = Object.entries(v)
          .map(([ik, iv]) => `${ik}: ${Array.isArray(iv) ? iv.join(', ') : String(iv)}`)
          .join('; ');
        return `${k}: { ${inner} }`;
      }
      return `${k}: ${String(v)}`;
    })
    .filter(Boolean);
  return entries.length ? entries.join(' • ') : '—';
}

/** Pull the job node inside a story that matches this job category */
function pickJobNode(story, jobName) {
  const arr = Array.isArray(story?.jobs_to_be_done) ? story.jobs_to_be_done : [];
  const norm = (s) => String(s || '').trim().toLowerCase();
  return arr.find(j => norm(j?.job_to_be_done_category) === norm(jobName)) || null;
}

/** Compute per-row score consistent with your tile variant */
function computePrimaryScore({ variant, importance, satisfaction, interviews = 1 }) {
  if (variant === 'win') {
    return computeWinScore({ importance, satisfaction, interviews });
  }
  // default to gap
  return computeGapScore({ importance, satisfaction });
}

/** Extract a reasonable quote from the job node */
function pickJobQuote(jobNode) {
  return (
    jobNode?.satisfaction_quote ||
    jobNode?.quote ||
    (Array.isArray(jobNode?.how_they_measure_satisfaction) &&
      (jobNode.how_they_measure_satisfaction[0]?.quote || '')) ||
    ''
  );
}

/**
 * Props:
 * - jobName: string (required)
 * - customerStories: array (required)
 * - variant: 'win' | 'gap' (from the header Job tile) — controls default sort
 */
export default function JobInterviewsTable({ jobName, customerStories = [], variant = 'gap' }) {
  const rows = React.useMemo(() => {
    const list = [];

    for (const s of customerStories || []) {
      const jobNode = pickJobNode(s, jobName);
      if (!jobNode) continue;

      const importance = Number(jobNode?.importance ?? 0) || 0;
      const satisfaction = Number(jobNode?.satisfaction ?? 0) || 0;
      const primary = computePrimaryScore({ variant, importance, satisfaction, interviews: 1 });

      list.push({
        key: `${s.customer_name || 'Interview'}-${s.conversation_date || ''}-${primary}`,
        interviewName: s.customer_name || 'Interview',
        date: s.conversation_date || '',
        segmentation: formatSegmentation(s.segmentation_data),
        tags: Array.isArray(s.custom_tags) ? s.custom_tags : (s.tags || []),
        rawJob: jobNode?.raw_job_to_be_done || jobNode?.job_to_be_done || '',
        importance,
        satisfaction,
        quote: pickJobQuote(jobNode),
        href: `/conversations?search=${encodeURIComponent(s.customer_name || '')}`, // swap to your modal if you want
        _score: primary
      });
    }

    // Default sort: mirror tile ranking
    return list.sort((a, b) => b._score - a._score);
  }, [customerStories, jobName, variant]);

  if (!rows.length) return null;

  return (
    <TableContainer component={Box} sx={{ mt: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Interview</TableCell>
            <TableCell>Segmentation</TableCell>
            <TableCell>Custom Tags</TableCell>
            <TableCell>Date</TableCell>

            {/* Object-specific columns for Job */}
            <TableCell>Raw Job</TableCell>
            <TableCell align="right">Importance</TableCell>
            <TableCell align="right">Satisfaction</TableCell>
            <TableCell>Quote</TableCell>
            <TableCell align="center" width={56}>Open</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.key} hover>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {r.interviewName || '—'}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {r.segmentation || '—'}
                </Typography>
              </TableCell>

              <TableCell>
                {Array.isArray(r.tags) && r.tags.length > 0 ? (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {r.tags.map((t, i) => (
                      <Chip key={`${t}-${i}`} size="small" label={String(t)} />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>—</Typography>
                )}
              </TableCell>

              <TableCell>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {r.date || '—'}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography variant="body2">{r.rawJob || '—'}</Typography>
              </TableCell>

              <TableCell align="right">
                {Number.isFinite(r.importance) ? F.jtbdImportance(r.importance).text : '—'}
              </TableCell>
              <TableCell align="right">
                {Number.isFinite(r.satisfaction) ? F.jtbdSatisfaction(r.satisfaction).text : '—'}
              </TableCell>

              <TableCell>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {r.quote || '—'}
                </Typography>
              </TableCell>

              <TableCell align="center">
                <Tooltip title="Open interview">
                  <span>
                    <IconButton
                      size="small"
                      component="a"
                      href={r.href}
                      target="_blank"
                      rel="noopener"
                      aria-label="Open interview"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
